/* eslint-disable */
import { useCallback, useEffect, useRef, useState } from 'react';
import { DropEvent, FileRejection } from 'react-dropzone';

import { useFormikContext } from 'formik';
import { get } from 'lodash';

import { postMedia } from '@common/api/media';

import { FileUploadErrorsCodes } from '../constants/types';
import FileWithProgress from '../types/fileWithProgress';

type PropsType = {
	multiple: boolean;
	name: string;
};

const useFileDrop = ({ multiple, name }: PropsType) => {
	const { setFieldValue, values } = useFormikContext<any>();
	const [files, setFiles] = useState<FileWithProgress[]>([]);
	const [isDragOver, setIsDragOver] = useState(false);
	const [errorType, setErrorType] = useState<string | null>(null);
	const uploadingFilesRef = useRef(new Set());

	const valueFiles = get(values, name) || [];

	const valueFilesWithProgress = (Array.isArray(valueFiles) ? valueFiles : [valueFiles]).map(
		(file: any) => ({
			url: file.url,
			progress: 100,
			name: file.original_name || file.name || file.url.split('/').pop(),
		}),
	);

	const onDrop = useCallback((acceptedFiles: File[]) => {
		setErrorType(null);
		const newFiles = acceptedFiles.map((file) => ({
			file,
			progress: 0,
			name: file.name,
		}));

		setFiles((prevFiles) => {
			if (multiple) {
				return [...prevFiles, ...newFiles];
			}
			return newFiles;
		});
	}, []);

	const onReject = (rejectedFiles: FileRejection[], event: DropEvent, maxSize?: number) => {
		const [file] = rejectedFiles;
		const errorCode = file?.errors?.[0].code;

		if (maxSize && errorCode === FileUploadErrorsCodes.LargeFile)
			setErrorType(`File is more than ${maxSize}mb`);
		else if (errorCode === FileUploadErrorsCodes.InvalidType)
			setErrorType(`${file.file.type} type is not supported.`);
		else setErrorType(`${file?.errors?.[0]?.message}`);

		setIsDragOver(false);
	};

	const uploadFile = async (fileWithProgress: FileWithProgress) => {
		if (!fileWithProgress.file) return;

		try {
			const response = await postMedia(fileWithProgress?.file, (progress) =>
				setFiles((prevFiles) =>
					prevFiles.map((f) => (f.file === fileWithProgress.file ? { ...f, progress } : f)),
				),
			);

			setFiles((prevFiles) =>
				prevFiles.map((f) => {
					if (f.file !== fileWithProgress.file) {
						return f;
					}
					return response?.file?.url
						? { ...f, url: response.file?.url, name: response.file?.original_name, progress: 100 }
						: { ...f, error: 'Something went wrong' };
				}),
			);
		} catch (error) {
			setFiles((prevFiles) =>
				prevFiles.map((f) =>
					f.file === fileWithProgress.file ? { ...f, error: error.message } : f,
				),
			);
		} finally {
			uploadingFilesRef.current.delete(fileWithProgress.file);
		}
	};

	const handleDelete = (fileOrUrl: File | string) => {
		setFieldValue(
			name,
			valueFilesWithProgress.filter((f) => f.url !== fileOrUrl),
		);
	};

	useEffect(() => {
		const filesToUpload = files.filter(
			(file) => file.file && !file.url && !file.error && file.progress === 0,
		);
		filesToUpload.forEach((fileWithProgress) => {
			if (!uploadingFilesRef.current.has(fileWithProgress.file)) {
				uploadingFilesRef.current.add(fileWithProgress.file);
				uploadFile(fileWithProgress);
			}
		});
	}, [files]);

	useEffect(() => {
		const successfulUploads = files
			.filter((f) => f?.url)
			.map((f) => ({ url: f?.url, name: f?.name }));
		if (!successfulUploads.length) {
			return;
		}
		setFieldValue(name, multiple ? successfulUploads : successfulUploads[0]);
	}, [files, setFieldValue, name, multiple]);

	const onDragEnter = () => {
		setIsDragOver(true);
	};

	const onDragLeave = () => {
		setIsDragOver(false);
	};

	return {
		onDrop,
		handleDelete,
		isDragOver,
		onDragEnter,
		onDragLeave,
		files: [...files.filter((file) => file.progress < 100), ...valueFilesWithProgress],
		onReject,
		errorType,
	};
};

export default useFileDrop;
